import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { APP_NAME } from '../utils/constants';
import { consumeDealerReturnPath, resolvePostAuthPath } from '../utils/dealerHostRouting';
import { useAuth } from '../contexts/AuthContext';
import { signInWithGoogle, logOut } from '../services/authService';
import { authenticatedFetch } from '../api/apiClient';
import { API_ROUTES } from '../shared/apiRoutes/apiRoutes';
import { backendApiUrl } from '../config/backendApiUrl';
import LoadingSpinner from '../components/common/LoadingSpinner';
import SupportFormModal from '../components/support/SupportFormModal';
import { ReadAloudTextButton } from './Training/components/ReadAloudButton';

const TOKEN_STORAGE_KEY = 'sb_onboard_invite_token';

const OnboardPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser, authReady, refreshAgreementStatus } = useAuth();
  const [token, setToken] = useState('');
  const [lookup, setLookup] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [inviteBound, setInviteBound] = useState(false);
  const [agreements, setAgreements] = useState(null);
  const [agreementStatus, setAgreementStatus] = useState(null);
  const [checked, setChecked] = useState({});
  const [acceptingType, setAcceptingType] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const consumeStartedRef = useRef(false);
  const agreementsLoadedRef = useRef(false);

  useEffect(() => {
    const fromQuery = (searchParams.get('token') || '').trim();
    if (fromQuery) {
      sessionStorage.setItem(TOKEN_STORAGE_KEY, fromQuery);
      setToken(fromQuery);
      return;
    }
    const stored = (sessionStorage.getItem(TOKEN_STORAGE_KEY) || '').trim();
    setToken(stored);
  }, [searchParams]);

  useEffect(() => {
    if (!token) {
      setLookup({ valid: false, reason: 'missing' });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(backendApiUrl(`${API_ROUTES.INVITES_LOOKUP}?token=${encodeURIComponent(token)}`));
        const json = await resp.json();
        if (!cancelled) setLookup(json?.data || { valid: false, reason: 'invalid' });
      } catch {
        if (!cancelled) setLookup({ valid: false, reason: 'invalid' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const loadAgreements = useCallback(async () => {
    const [requiredResp, statusResp] = await Promise.all([
      authenticatedFetch(API_ROUTES.AGREEMENTS_ME_REQUIRED),
      authenticatedFetch(API_ROUTES.AGREEMENTS_ME_STATUS),
    ]);
    const requiredJson = await requiredResp.json();
    const statusJson = await statusResp.json();
    if (!requiredResp.ok) {
      throw new Error(requiredJson.error || 'Could not load agreements');
    }
    const items = requiredJson?.data || [];
    const status = statusJson?.data || { complete: true };
    setAgreements(items);
    setAgreementStatus(status);
    if (refreshAgreementStatus) {
      await refreshAgreementStatus();
    }
    return { items, status };
  }, [refreshAgreementStatus]);

  useEffect(() => {
    if (!authReady || !currentUser) return undefined;
    if (lookup === null) return undefined;

    const canConsume = Boolean(token) && lookup?.valid === true;

    if (canConsume && !consumeStartedRef.current && !inviteBound) {
      consumeStartedRef.current = true;
      let cancelled = false;
      (async () => {
        setBusy(true);
        setError(null);
        try {
          const resp = await authenticatedFetch(API_ROUTES.INVITES_CONSUME, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
          });
          const json = await resp.json();
          if (!resp.ok) {
            throw new Error(json.error || 'Could not complete invitation');
          }
          sessionStorage.removeItem(TOKEN_STORAGE_KEY);
          try {
            await currentUser.getIdToken(true);
          } catch {
            /* claims refresh is best-effort */
          }
          if (!cancelled) {
            setInviteBound(true);
            agreementsLoadedRef.current = true;
            await loadAgreements();
          }
        } catch (err) {
          consumeStartedRef.current = false;
          if (!cancelled) {
            setError(err.message || 'Could not complete invitation');
          }
        } finally {
          if (!cancelled) setBusy(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    if (!canConsume && currentUser && !agreementsLoadedRef.current) {
      agreementsLoadedRef.current = true;
      let cancelled = false;
      (async () => {
        setBusy(true);
        try {
          await loadAgreements();
        } catch (err) {
          if (!cancelled) {
            agreementsLoadedRef.current = false;
            setError(err.message || 'Could not load agreements');
          }
        } finally {
          if (!cancelled) setBusy(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    return undefined;
  }, [authReady, currentUser, token, lookup, inviteBound, loadAgreements]);

  const handleGoogleSignIn = async () => {
    setBusy(true);
    setError(null);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(err.message || 'Google sign-in failed');
      setBusy(false);
    }
  };

  const handleAccept = async (agreementType) => {
    if (!checked[agreementType]) return;
    setAcceptingType(agreementType);
    setError(null);
    try {
      const resp = await authenticatedFetch(API_ROUTES.AGREEMENTS_ME_ACCEPT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agreementType }),
      });
      const json = await resp.json();
      if (!resp.ok) {
        throw new Error(json.error || 'Could not record acceptance');
      }
      const nextStatus = json?.data?.status;
      if (nextStatus) setAgreementStatus(nextStatus);
      setAgreements((prev) =>
        (prev || []).map((item) =>
          item.agreementType === agreementType ? { ...item, accepted: true } : item
        )
      );
      if (refreshAgreementStatus) {
        await refreshAgreementStatus();
      }
    } catch (err) {
      setError(err.message || 'Could not record acceptance');
    } finally {
      setAcceptingType(null);
    }
  };

  const handleContinue = async () => {
    if (refreshAgreementStatus) {
      await refreshAgreementStatus();
    }
    navigate(
      resolvePostAuthPath({
        hostname: typeof window !== 'undefined' ? window.location.hostname : '',
        agreementComplete: true,
        returnPath: consumeDealerReturnPath(),
      }),
      { replace: true }
    );
  };

  const lookupInvalid = lookup && lookup.valid === false;
  const inviteUnusable =
    lookupInvalid && token && !currentUser && lookup.reason !== 'missing';
  const outstanding = (agreements || []).filter((item) => !item.accepted);
  const allAccepted =
    agreementStatus?.complete === true || (agreements !== null && outstanding.length === 0);
  const showAgreements = Boolean(currentUser) && agreements !== null && !allAccepted;
  const showComplete = Boolean(currentUser) && agreements !== null && allAccepted;

  if (!authReady || (token && lookup === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <LoadingSpinner text="Loading invitation..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 py-8 px-4">
      <div className="max-w-2xl w-full p-6 bg-white rounded-lg shadow-md">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">{APP_NAME}</h1>
          {!showComplete && !showAgreements && (
            <p className="text-gray-600">
              Sign in with the Google account for this invitation. Signal Bridge does not use passwords.
            </p>
          )}
        </div>

        {inviteUnusable && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
            This invitation is{' '}
            {lookup.reason === 'expired'
              ? 'expired'
              : lookup.reason === 'consumed'
                ? 'already used'
                : 'not valid'}
            .
          </div>
        )}

        {!token && !currentUser && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
            This invitation link is missing a token.
          </div>
        )}

        {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">{error}</div>}

        {lookup?.valid && lookup.email && !showComplete && (
          <p className="mb-4 text-sm text-gray-700">
            Invited email: <span className="font-medium">{lookup.email}</span>
          </p>
        )}

        {!currentUser && !inviteUnusable && token && (
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={busy || !lookup}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-4 py-2 rounded-md"
          >
            {busy ? 'Signing in...' : 'Continue with Google'}
          </button>
        )}

        {currentUser && !showAgreements && !showComplete && (
          <div className="flex items-center justify-center py-4">
            <LoadingSpinner size={8} text={busy ? 'Completing invitation...' : 'Loading agreements...'} />
          </div>
        )}

        {showAgreements && outstanding[0] && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Review and accept this required agreement
              {outstanding.length > 1 ? ` (${outstanding.length} remaining)` : ''}.
            </p>
            {(() => {
              const item = outstanding[0];
              return (
                <section
                  key={item.agreementType}
                  className="border border-gray-200 rounded-md p-4 bg-gray-50"
                >
                  <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-3 mb-1">
                    <h2 className="text-lg font-semibold text-gray-900 leading-snug">{item.title}</h2>
                    <span className="text-xs text-gray-500 shrink-0">Version {item.version}</span>
                  </div>
                  <ReadAloudTextButton
                    text={[item.title, item.body].filter(Boolean).join('\n\n')}
                    sx={{ mb: 1, px: 0, textTransform: 'none' }}
                  >
                    Read aloud
                  </ReadAloudTextButton>
                  <div className="prose prose-sm max-w-none bg-white border border-gray-200 rounded p-4 mb-4 max-h-[50vh] overflow-y-auto text-gray-800">
                    <ReactMarkdown>{item.body || ''}</ReactMarkdown>
                  </div>
                  <label className="flex items-start gap-2 text-sm text-gray-800 mb-3">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded"
                      checked={!!checked[item.agreementType]}
                      onChange={(e) =>
                        setChecked((prev) => ({
                          ...prev,
                          [item.agreementType]: e.target.checked,
                        }))
                      }
                    />
                    <span>I have read and agree to this agreement</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Accepting as {currentUser.displayName || currentUser.email}
                  </p>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <button
                      type="button"
                      disabled={!checked[item.agreementType] || acceptingType === item.agreementType}
                      onClick={() => handleAccept(item.agreementType)}
                      className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2.5 rounded-md text-sm font-medium"
                    >
                      {acceptingType === item.agreementType ? 'Accepting...' : 'Accept'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSupportOpen(true)}
                      className="w-full sm:w-auto border border-gray-300 text-gray-800 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-white"
                    >
                      Support ticket
                    </button>
                  </div>
                </section>
              );
            })()}
          </div>
        )}

        {showComplete && (
          <div className="text-center py-4">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Agreements Complete</h2>
            <p className="text-gray-600 mb-6">Your Signal Bridge account is ready.</p>
            <button
              type="button"
              onClick={handleContinue}
              className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-md"
            >
              Continue to Signal Bridge
            </button>
          </div>
        )}

        <SupportFormModal
          isOpen={supportOpen}
          onClose={() => setSupportOpen(false)}
          initialType="question"
          initialTitle="Help with a required agreement"
          initialDescription="I am on the required agreement screen and need help before I can continue."
        />

        {currentUser && error && (
          <button
            type="button"
            onClick={async () => {
              consumeStartedRef.current = false;
              await logOut();
              setError(null);
              setBusy(false);
              setInviteBound(false);
              setAgreements(null);
              setAgreementStatus(null);
              agreementsLoadedRef.current = false;
            }}
            className="mt-3 w-full border border-gray-300 text-gray-700 px-4 py-2 rounded-md"
          >
            Use a different Google account
          </button>
        )}
      </div>
    </div>
  );
};

export default OnboardPage;
